import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Loader2, StickyNote, RefreshCw } from "lucide-react";
import { formatBRL, waLink } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMNS = [
  { id: "novo", label: "Novo", ring: "border-emerald-500/40" },
  { id: "em contato", label: "Em contato", ring: "border-sky-500/40" },
  { id: "visita", label: "Visita agendada", ring: "border-violet-500/40" },
  { id: "proposta", label: "Proposta", ring: "border-amber-500/40" },
  { id: "fechado", label: "Fechado", ring: "border-emerald-400/60" },
  { id: "perdido", label: "Perdido", ring: "border-red-500/40" },
];

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return "agora";
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
};

const inputCls =
  "w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

export const FunilTab = ({ onUnauthorized }) => {
  const [leads, setLeads] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saleFor, setSaleFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const load = useCallback(async () => {
    try {
      const [l, v, s] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/vehicles/admin`, { headers }),
        axios.get(`${API}/sellers`, { headers }),
      ]);
      setLeads(l.data);
      setVehicles(v.data);
      setSellers(s.data);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else { setLeads([]); toast.error("Erro ao carregar funil."); }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vehicleName = (id) => vehicles.find((v) => v.id === id)?.nome;
  const sellerById = (id) => sellers.find((s) => s.id === id);
  const vendedoresAtivos = sellers.filter((s) => s.ativo);
  const disponiveis = vehicles.filter((v) => v.status !== "vendido");

  const openSale = (lead) => {
    const s = sellerById(lead.vendedor_id);
    const base = Number(lead.valor ?? lead.valor_negociado ?? 0);
    setSaleFor({ ...lead, valor: base || "", comissao: s && base ? Math.round(base * s.comissao_pct) / 100 : (lead.comissao || "") });
  };

  const onSellerChange = (sid) => {
    const s = sellerById(sid);
    const base = Number(saleFor.valor ?? saleFor.valor_negociado ?? 0);
    setSaleFor({ ...saleFor, vendedor_id: sid || null, comissao: s && base ? Math.round(base * s.comissao_pct) / 100 : "" });
  };

  const moveTo = async (lead, status) => {
    if ((lead.status || "novo") === status) return;
    if (status === "fechado") {
      openSale(lead);
      return;
    }
    try {
      await axios.patch(`${API}/leads/${lead.id}`, { status }, { headers });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success(`Movido para "${COLUMNS.find((c) => c.id === status)?.label}".`);
    } catch { toast.error("Erro ao mover negócio."); }
  };

  const saveDetail = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/leads/${selected.id}`,
        { veiculo_id: selected.veiculo_id || null, vendedor_id: selected.vendedor_id || null, valor_negociado: selected.valor_negociado ? Number(selected.valor_negociado) : null, status: selected.status },
        { headers }
      );
      setLeads((ls) => ls.map((l) => (l.id === selected.id ? res.data : l)));
      setSelected(null);
      toast.success("Negócio atualizado.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const res = await axios.post(`${API}/leads/${selected.id}/notas`, { texto: noteText }, { headers });
      setSelected(res.data);
      setLeads((ls) => ls.map((l) => (l.id === selected.id ? res.data : l)));
      setNoteText("");
    } catch { toast.error("Erro ao adicionar nota."); }
  };

  const registerSale = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/sales`, {
        lead_id: saleFor.id,
        veiculo_id: saleFor.veiculo_id,
        vendedor_id: saleFor.vendedor_id || null,
        valor: Number(saleFor.valor ?? saleFor.valor_negociado ?? 0),
        comissao: Number(saleFor.comissao || 0),
        imposto_pct: Number(saleFor.imposto_pct || 0),
        cliente: saleFor.cliente ?? saleFor.nome,
      }, { headers });
      toast.success("Venda registrada! Estoque, financeiro e lucro atualizados.");
      setSaleFor(null);
      setSelected(null);
      load();
    } catch { toast.error("Erro ao registrar venda."); }
    finally { setSaving(false); }
  };

  const onDrop = (e, colId) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("leadId");
    const lead = leads?.find((l) => l.id === id);
    if (lead) moveTo(lead, colId);
  };

  return (
    <div data-testid="funil-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-white">Funil de vendas</h1>
        <button onClick={load} data-testid="funil-refresh" className="inline-flex items-center gap-2 glass hover:border-emerald-500/50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </div>

      {leads === null ? (
        <div className="h-64 rounded-2xl bg-[#131620] animate-pulse" />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="funil-board">
          {COLUMNS.map((col) => {
            const items = leads.filter((l) => (l.status || "novo") === col.id);
            return (
              <div
                key={col.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, col.id)}
                className={`min-w-[250px] w-[250px] shrink-0 rounded-2xl bg-[#0d0f17] border ${col.ring} p-3`}
                data-testid={`funil-col-${col.id.replace(/\s/g, "-")}`}
              >
                <div className="flex items-center justify-between px-1 pb-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-300">{col.label}</p>
                  <span className="text-xs font-mono2 text-slate-500">{items.length}</span>
                </div>
                <div className="space-y-2.5 min-h-[60px]">
                  {items.map((l) => (
                    <motion.div
                      key={l.id}
                      layout
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("leadId", l.id)}
                      onClick={() => setSelected({ ...l })}
                      className="glass rounded-xl p-3.5 cursor-grab active:cursor-grabbing hover:border-emerald-500/40 transition-colors"
                      data-testid={`funil-card-${l.id}`}
                    >
                      <p className="text-sm font-semibold text-white truncate">{l.nome}</p>
                      <p className="text-xs text-slate-400 truncate mt-0.5">{vehicleName(l.veiculo_id) || l.interesse}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-slate-500">{timeAgo(l.created_at)}</span>
                        {l.valor_negociado > 0 && (
                          <span className="text-[11px] font-semibold text-emerald-400">{formatBRL(l.valor_negociado)}</span>
                        )}
                      </div>
                      {sellerById(l.vendedor_id) && (
                        <p className="mt-1.5 text-[10px] font-semibold text-sky-400 truncate" data-testid={`funil-card-seller-${l.id}`}>
                          {sellerById(l.vendedor_id).nome}
                        </p>
                      )}
                      <select
                        value={l.status || "novo"}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => moveTo(l, e.target.value)}
                        data-testid={`funil-card-status-${l.id}`}
                        className="mt-2 w-full text-[11px] bg-[#0d0f17] border border-slate-800 rounded-lg px-2 py-1.5 text-slate-300 outline-none cursor-pointer"
                      >
                        {COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                    </motion.div>
                  ))}
                  {items.length === 0 && <p className="text-[11px] text-slate-600 text-center py-4">Arraste negócios aqui</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {selected && !saleFor && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
            data-testid="funil-detail-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl p-7 w-full max-w-lg max-h-[85vh] overflow-y-auto space-y-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-display text-xl font-bold text-white">{selected.nome}</h2>
                  <p className="text-xs text-slate-400">{selected.whatsapp} {selected.email ? `· ${selected.email}` : ""}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-white" data-testid="funil-detail-close"><X className="w-5 h-5" /></button>
              </div>
              {selected.mensagem && <p className="text-sm text-slate-400 bg-[#0d0f17] rounded-xl p-3">"{selected.mensagem}"</p>}

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-slate-400 block mb-1.5">Veículo de interesse</label>
                  <select
                    value={selected.veiculo_id || ""}
                    onChange={(e) => setSelected({ ...selected, veiculo_id: e.target.value })}
                    className={inputCls} data-testid="funil-detail-veiculo"
                  >
                    <option value="">Nenhum vinculado</option>
                    {disponiveis.map((v) => <option key={v.id} value={v.id}>{v.nome} — {formatBRL(v.preco)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">Valor negociado (R$)</label>
                  <input
                    type="number" className={inputCls} value={selected.valor_negociado || ""}
                    onChange={(e) => setSelected({ ...selected, valor_negociado: e.target.value })}
                    data-testid="funil-detail-valor"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">Vendedor responsável</label>
                  <select
                    value={selected.vendedor_id || ""}
                    onChange={(e) => setSelected({ ...selected, vendedor_id: e.target.value || null })}
                    className={inputCls} data-testid="funil-detail-vendedor"
                  >
                    <option value="">Sem vendedor</option>
                    {vendedoresAtivos.map((s) => <option key={s.id} value={s.id}>{s.nome} ({s.comissao_pct}%)</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">Etapa</label>
                  <select
                    value={selected.status || "novo"}
                    onChange={(e) => e.target.value === "fechado" ? openSale({ ...selected, status: "fechado" }) : setSelected({ ...selected, status: e.target.value })}
                    className={inputCls} data-testid="funil-detail-status"
                  >
                    {COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400 flex items-center gap-1.5 mb-1.5"><StickyNote className="w-3.5 h-3.5" /> Notas</label>
                <div className="space-y-2 max-h-32 overflow-y-auto mb-2">
                  {(selected.notas || []).map((n, i) => (
                    <p key={i} className="text-xs text-slate-300 bg-[#0d0f17] rounded-lg px-3 py-2">
                      {n.texto} <span className="text-slate-600">· {new Date(n.created_at).toLocaleDateString("pt-BR")}</span>
                    </p>
                  ))}
                  {(selected.notas || []).length === 0 && <p className="text-xs text-slate-600">Sem notas ainda.</p>}
                </div>
                <div className="flex gap-2">
                  <input className={inputCls} placeholder="Adicionar nota..." value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="funil-note-input" />
                  <button onClick={addNote} className="shrink-0 glass hover:border-emerald-500/50 text-white text-xs font-semibold px-4 rounded-xl" data-testid="funil-note-add">OK</button>
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  onClick={saveDetail} disabled={saving} data-testid="funil-detail-save"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-5 py-3 rounded-full transition-colors"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
                </button>
                <a
                  href={waLink(`Olá ${selected.nome}! Aqui é da Seed Motors.`)}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 glass hover:border-emerald-500/50 text-white font-semibold px-5 py-3 rounded-full"
                  data-testid="funil-detail-wa"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-400" /> WhatsApp
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}

        {saleFor && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSaleFor(null)}
            data-testid="sale-modal"
          >
            <motion.form
              onSubmit={registerSale}
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl p-7 w-full max-w-md space-y-4"
            >
              <h2 className="font-display text-xl font-bold text-white">Registrar venda</h2>
              <p className="text-xs text-slate-400">O veículo sai do estoque e o valor entra no financeiro automaticamente.</p>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5">Cliente</label>
                <input className={inputCls} value={saleFor.cliente ?? saleFor.nome} onChange={(e) => setSaleFor({ ...saleFor, cliente: e.target.value })} data-testid="sale-cliente" />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5">Veículo vendido*</label>
                <select required className={inputCls} value={saleFor.veiculo_id || ""} onChange={(e) => setSaleFor({ ...saleFor, veiculo_id: e.target.value })} data-testid="sale-veiculo">
                  <option value="">Selecione...</option>
                  {disponiveis.map((v) => <option key={v.id} value={v.id}>{v.nome} — {formatBRL(v.preco)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5">Vendedor</label>
                <select className={inputCls} value={saleFor.vendedor_id || ""} onChange={(e) => onSellerChange(e.target.value)} data-testid="sale-vendedor">
                  <option value="">Sem vendedor</option>
                  {vendedoresAtivos.map((s) => <option key={s.id} value={s.id}>{s.nome} ({s.comissao_pct}%)</option>)}
                </select>
                {sellerById(saleFor.vendedor_id) && (
                  <p className="text-[11px] text-emerald-400 mt-1.5 px-1" data-testid="sale-comissao-hint">
                    Comissão calculada: {sellerById(saleFor.vendedor_id).comissao_pct}% do valor — ajuste abaixo se houver desconto.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">Valor final (R$)*</label>
                  <input required type="number" className={inputCls} value={saleFor.valor ?? (saleFor.valor_negociado || "")} onChange={(e) => setSaleFor({ ...saleFor, valor: e.target.value })} data-testid="sale-valor" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">Comissão (R$)</label>
                  <input type="number" className={inputCls} value={saleFor.comissao || ""} onChange={(e) => setSaleFor({ ...saleFor, comissao: e.target.value })} data-testid="sale-comissao" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">Imposto/taxa (%)</label>
                  <input type="number" step="0.1" className={inputCls} value={saleFor.imposto_pct || ""} onChange={(e) => setSaleFor({ ...saleFor, imposto_pct: e.target.value })} data-testid="sale-imposto" placeholder="0" />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">O lucro líquido (venda − custo total do carro − comissão − imposto) é calculado e salvo automaticamente.</p>
              <div className="flex gap-3">
                <button type="submit" disabled={saving} data-testid="sale-submit" className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-5 py-3 rounded-full transition-colors">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar venda
                </button>
                <button type="button" onClick={() => setSaleFor(null)} className="glass text-white text-sm font-semibold px-5 py-3 rounded-full">Cancelar</button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
